import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Search } from "lucide-react";
import { staffApi as api } from "../../lib/staffApi";
import { Input } from "../ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";

const PLAN_CLS = { starter: "bg-stone-100 text-stone-600 border-stone-300", trial: "bg-teal-50 text-teal-700 border-teal-300", premium: "bg-amber-50 text-amber-700 border-amber-400" };
const SOURCE = { grant: "Granted (free)", stripe: "Paid (Stripe)", trial: "Trial" };
const FILTERS = [["all", "All"], ["starter", "Starter"], ["trial", "Trial"], ["premium", "Premium"]];

const PlanCell = ({ u }) => (
  <div className="flex flex-col gap-1">
    <span className={`self-start px-2 py-0.5 rounded-full border text-xs font-bold capitalize ${PLAN_CLS[u.plan]}`} data-testid={`user-plan-${u.user_id}`}>{u.plan}</span>
    <span className="text-xs text-stone-500">{u.source ? SOURCE[u.source] : "—"}
      {u.plan === "trial" && ` · until ${u.trial_ends_at.slice(0, 10)}`}
      {u.source === "stripe" && u.subscription && ` · ${u.subscription.interval}ly · ${u.subscription.status}`}</span>
  </div>
);

export const UsersPanel = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [f, setF] = useState("all");
  const [confirm, setConfirm] = useState(null);
  const load = (query = q) => api.get("/users", { params: { q: query } }).then(({ data }) => setRows(data));
  useEffect(() => { load(""); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = async () => {
    const { user, grant } = confirm;
    setConfirm(null);
    try {
      await api.post(`/users/${user.user_id}/premium`, { grant });
      toast.success(grant ? `Premium granted to ${user.email}` : `Granted Premium removed from ${user.email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    load();
  };
  const shown = rows.filter((u) => f === "all" || u.plan === f);
  const count = (k) => rows.filter((u) => u.plan === k).length;
  return (
    <div className="space-y-5" data-testid="admin-users">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {FILTERS.map(([k, l]) => (
          <button key={k} data-testid={`users-filter-${k}`} onClick={() => setF(k)} className={`scroll-card p-4 text-left ${f === k ? "ring-2 ring-[var(--vermilion)]" : ""}`}>
            <div className="text-xs uppercase text-stone-500">{l}</div><div className="text-3xl font-bold text-[var(--tea)] mt-1" data-testid={`users-count-${k}`}>{k === "all" ? rows.length : count(k)}</div>
          </button>
        ))}
      </div>
      <form className="flex gap-2 max-w-md" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <Input data-testid="users-search-input" placeholder="Search email or name" value={q} onChange={(e) => setQ(e.target.value)} />
        <button data-testid="users-search-btn" className="h-10 px-4 rounded-full bg-[var(--ink)] text-white text-sm flex items-center gap-1"><Search size={14} /> Search</button>
      </form>
      <div className="scroll-card overflow-auto">
        <Table>
          <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>Access</TableHead><TableHead>Learners</TableHead><TableHead>Joined</TableHead><TableHead className="text-right">Free Premium</TableHead></TableRow></TableHeader>
          <TableBody>
            {shown.map((u) => (
              <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                <TableCell><div className="font-semibold">{u.name || "—"}</div><div className="text-xs text-stone-500">{u.email}</div></TableCell>
                <TableCell className="capitalize">{u.role}</TableCell>
                <TableCell><PlanCell u={u} /></TableCell>
                <TableCell>{u.children}</TableCell>
                <TableCell className="text-xs text-stone-500">{(u.created_at || "").slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  {u.premium_grant ? <button data-testid={`user-revoke-${u.user_id}`} onClick={() => setConfirm({ user: u, grant: false })} className="h-9 px-4 rounded-full border-2 border-stone-300 text-sm font-semibold">Remove</button>
                    : <button data-testid={`user-grant-${u.user_id}`} onClick={() => setConfirm({ user: u, grant: true })} className="h-9 px-4 rounded-full bg-amber-500 text-white text-sm font-semibold inline-flex items-center gap-1"><Crown size={14} /> Grant Premium</button>}
                </TableCell>
              </TableRow>
            ))}
            {!shown.length && <TableRow><TableCell colSpan={6} className="text-center text-stone-400 py-8">No users</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="users-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.grant ? "Grant permanent Premium?" : "Remove granted Premium?"}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.grant ? `${confirm?.user.email} will get full Premium access for free, with no payment and no end date.` : `${confirm?.user.email} will go back to their trial, Stripe subscription or the free Starter plan.`}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="users-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="users-confirm-ok" onClick={apply}>{confirm?.grant ? "Grant" : "Remove"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const FIELDS = [["monthly_price", "Monthly price (USD)", 0.01], ["yearly_price", "Yearly price (USD)", 0.01], ["trial_days", "Trial days", 1],
  ["free_classic", "Free classic stories", 1], ["free_life_themes", "Free life themes", 1], ["free_speak", "Free speak lessons", 1],
  ["free_children", "Learners on Starter", 1], ["premium_children", "Learners on Premium", 1]];

export const BillingPanel = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/billing").then(({ data }) => setS(data)); }, []);
  if (!s) return null;
  const save = async () => {
    try {
      const { data } = await api.put("/billing", Object.fromEntries(FIELDS.map(([k]) => [k, Number(s[k])])));
      setS(data);
      toast.success("Plan settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="scroll-card p-6 max-w-2xl space-y-5" data-testid="admin-billing">
      <h3 className="font-bold">Plans &amp; limits</h3>
      <p className="text-sm text-stone-500">Price changes create new Stripe prices for new subscribers. Existing subscribers keep their current price.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map(([k, l, step]) => (
          <label key={k} className="text-sm text-stone-600 space-y-1"><span>{l}</span>
            <Input data-testid={`billing-${k}-input`} type="number" step={step} min={0} value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} />
          </label>
        ))}
      </div>
      <button data-testid="billing-save-btn" onClick={save} className="h-10 px-5 rounded-full bg-[var(--vermilion)] text-white text-sm font-semibold">Save</button>
    </div>
  );
};
